﻿package com.mgacreative.hotelru.domain.repository

import com.mgacreative.hotelru.core.network.SupabaseNetworkClient
import com.mgacreative.hotelru.core.tenant.TenantSession
import io.github.jan.supabase.postgrest.postgrest
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.serialization.Serializable

@Serializable
data class InventoryItem(
    val id: String = "",
    val tenant_id: String = "",
    val name: String,
    val category: String,
    val quantity: Int = 0,
    val min_threshold: Int = 5,
    val unit: String = "Adet",
    val created_at: String? = null
)

@Serializable
data class InventoryTransaction(
    val id: String = "",
    val item_id: String,
    val tenant_id: String = "",
    val type: String, // ADD, REDUCE
    val quantity: Int,
    val description: String? = null,
    val created_at: String? = null
)

interface InventoryRepository {
    fun observeInventoryItems(): Flow<List<InventoryItem>>
    suspend fun loadInventoryItems()
    suspend fun addInventoryItem(name: String, category: String, quantity: Int, minThreshold: Int, unit: String)
    suspend fun updateInventoryItem(item: InventoryItem)
    suspend fun deleteInventoryItem(itemId: String)
    suspend fun addTransaction(itemId: String, type: String, quantity: Int, description: String?)
}

class InventoryRepositoryImpl(
    private val networkClient: SupabaseNetworkClient,
    private val tenantSession: TenantSession
) : InventoryRepository {

    private val _items = MutableStateFlow<List<InventoryItem>>(emptyList())

    override fun observeInventoryItems(): Flow<List<InventoryItem>> = _items.asStateFlow()

    override suspend fun loadInventoryItems() {
        try {
            val tenantId = tenantSession.currentTenantId
            val result = if (!tenantId.isNullOrBlank()) {
                networkClient.client.postgrest["inventory_items"]
                    .select {
                        filter { eq("tenant_id", tenantId) }
                    }
                    .decodeList<InventoryItem>()
            } else {
                networkClient.client.postgrest["inventory_items"]
                    .select()
                    .decodeList<InventoryItem>()
            }
            if (result.isNotEmpty()) {
                _items.value = result.sortedBy { it.name }
            }
        } catch (e: Exception) {
            e.printStackTrace()
            // Keep local items intact on error
        }
    }

    override suspend fun addInventoryItem(name: String, category: String, quantity: Int, minThreshold: Int, unit: String) {
        val tenantId = tenantSession.currentTenantId
        val isValidTenantUuid = tenantId != null && tenantId.contains("-") && tenantId.length >= 32
        val newItem = InventoryItem(
            id = "inv_${kotlin.random.Random.nextInt(10000, 99999)}",
            tenant_id = if (isValidTenantUuid) tenantId!! else "",
            name = name,
            category = category,
            quantity = quantity,
            min_threshold = minThreshold,
            unit = unit
        )
        // Add to local state immediately so UI updates
        _items.value = (_items.value + newItem).sortedBy { it.name }

        if (isValidTenantUuid) {
            try {
                @Serializable
                data class ItemRequest(
                    val tenant_id: String,
                    val name: String,
                    val category: String,
                    val quantity: Int,
                    val min_threshold: Int,
                    val unit: String
                )
                val req = ItemRequest(tenantId!!, name, category, quantity, minThreshold, unit)
                networkClient.client.postgrest["inventory_items"].insert(req)
                loadInventoryItems()
            } catch (e: Exception) {
                e.printStackTrace()
            }
        }
    }

    override suspend fun updateInventoryItem(item: InventoryItem) {
        _items.value = _items.value.map { if (it.id == item.id) item else it }
        try {
            networkClient.client.postgrest["inventory_items"]
                .update(item) {
                    filter { eq("id", item.id) }
                }
            loadInventoryItems()
        } catch (e: Exception) {
            e.printStackTrace()
        }
    }

    override suspend fun deleteInventoryItem(itemId: String) {
        _items.value = _items.value.filter { it.id != itemId }
        try {
            networkClient.client.postgrest["inventory_items"]
                .delete {
                    filter { eq("id", itemId) }
                }
            loadInventoryItems()
        } catch (e: Exception) {
            e.printStackTrace()
        }
    }

    override suspend fun addTransaction(itemId: String, type: String, quantity: Int, description: String?) {
        val targetItem = _items.value.find { it.id == itemId } ?: return
        val newQty = if (type == "ADD") targetItem.quantity + quantity else maxOf(0, targetItem.quantity - quantity)
        val updatedItem = targetItem.copy(quantity = newQty)
        updateInventoryItem(updatedItem)

        try {
            val tenantId = tenantSession.currentTenantId ?: "default_tenant"
            @Serializable
            data class TxRequest(
                val item_id: String,
                val tenant_id: String,
                val type: String,
                val quantity: Int,
                val description: String?
            )
            val req = TxRequest(itemId, tenantId, type, quantity, description)
            networkClient.client.postgrest["inventory_transactions"].insert(req)
        } catch (e: Exception) {
            e.printStackTrace()
        }
    }
}

